import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from '../../services/api-axios';
import OnlineUsersWrapper from './OnlineUsersWrapper';
import LobbyRoom from './LobbyRoom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { connectToLobby, disconnectFromLobby } from '../../store/lobbySlice';
import { dismiss } from '../../store/inviteSlice';
import { subscribe, whenConnected } from '../../services/ws-service';
import { leaveLobby, lobbyTopic } from '../../services/lobby-service';
import useLobbyMessageHandler from '../../message-handler/useLobbyMessageHandler';
import LobbyTable from './LobbyTable';
import CreateLobbyForm from './CreateLobbyForm';
import styles from './Lobby.module.css';

const SUITS = ['♠', '♥', '♦', '♣'];

const Lobby = () => {
    const lobbyId = useAppSelector((state) => state.lobby.lobbyId);
    const lobbyName = useAppSelector((state) => state.lobby.lobbyName);
    const invitor = useAppSelector((state) => state.invite.invitor);
    const invitedToLobbyId = useAppSelector((state) => state.invite.lobbyId);
    const onlineCount = useAppSelector((state) => state.onlineUsers.users.length);
    const username = useAppSelector((state) => state.onlineUsers.username);
    const activeLobbiesCount = useAppSelector((state) => state.lobby.lobbiesList.length);
    const onLobbyMessage = useLobbyMessageHandler();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [inviteError, setInviteError] = useState<string | null>(null);
    const autoJoinStarted = useRef(false);

    // Completes a /join/:inviteCode arrival. This runs here rather than on the
    // landing page because subscribing needs a live STOMP client, which only
    // exists once WsContainer (this route's parent) has connected.
    useEffect(() => {
        const pendingInviteCode = (location.state as { pendingInviteCode?: string } | null)?.pendingInviteCode;
        if (!pendingInviteCode || autoJoinStarted.current) return;
        autoJoinStarted.current = true;

        // Drop the code from history so a refresh or back-navigation does not re-join.
        // Absolute path, not '.', which react-router would resolve to "/home?index".
        navigate('/home', { replace: true, state: null });

        const joinFromInvite = async () => {
            try {
                const res = await axios.post(`/rest/lobby/invite/${pendingInviteCode}/join`);
                const joinedLobbyId: string = res.data.lobbyId;

                await whenConnected();
                subscribe(lobbyTopic(joinedLobbyId), onLobbyMessage);
                dispatch(connectToLobby(joinedLobbyId));
            } catch (err: any) {
                setInviteError(
                    err?.response?.data?.message ?? 'Could not join that lobby. Please ask for a new link.'
                );
            }
        };

        joinFromInvite();
    }, [location.state, navigate, dispatch, onLobbyMessage]);

    useEffect(() => {
        if (inviteError) {
            toast.error(inviteError, { position: 'top-right', autoClose: 6000 });
            setInviteError(null);
        }
    }, [inviteError]);

    useEffect(() => {
        if (invitedToLobbyId) {
            const Msg = ({ closeToast }: { closeToast?: () => void }) => (
                <div className={styles.invitationToast}>
                    <div className={styles.invitationTitle}>🎮 Lobby Invitation</div>
                    <p className={styles.invitationText}>
                        You were invited to join <span className={styles.invitationInvitor}>{invitor}</span>'s lobby!
                    </p>
                    <div className={styles.invitationActions}>
                        <button
                            className={styles.acceptButton}
                            onClick={() => {
                                acceptInvite(invitedToLobbyId);
                                closeToast?.();
                            }}
                        >
                            Accept
                        </button>
                        <button className={styles.declineButton} onClick={closeToast}>
                            Decline
                        </button>
                    </div>
                </div>
            );
            toast(Msg, {
                position: "top-right",
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                style: {
                    background: 'transparent',
                    boxShadow: 'none'
                }
            });
            dispatch(dismiss());
        }
    }, [invitedToLobbyId]);

    const acceptInvite = async (lobbyIdToJoin: string) => {
        if (lobbyIdToJoin === lobbyId) return;

        // The lobby we are in has to be released before the join, or the server
        // ends up holding us in both and can let go of neither.
        await leaveLobby(lobbyId, username);
        dispatch(disconnectFromLobby());

        try {
            await axios.post('/rest/lobby/join/', {
                invitor: 'marjan',
                lobbyId: lobbyIdToJoin
            });
            subscribe(lobbyTopic(lobbyIdToJoin), onLobbyMessage);
            dispatch(connectToLobby(lobbyIdToJoin));
        } catch (error) {
            console.error(error);
            setInviteError('That lobby is no longer available.');
        }
    };

    const handleLeaveLobby = (lobbyIdToLeave?: string) => {
        // Dropping the subscription is the leave: the server has no leave endpoint,
        // it releases us from the lobby when the subscription ends.
        leaveLobby(lobbyIdToLeave, username);
        dispatch(disconnectFromLobby());
    };

    return (
        <div className={styles.container}>
            <div className={styles.backgroundParticles} />

            <div className={styles.suitField} aria-hidden="true">
                {Array.from({ length: 8 }, (_, i) => (
                    <span
                        key={i}
                        className={styles.suit}
                        style={{
                            left: `${(i * 43) % 100}%`,
                            animationDuration: `${20 + (i % 5) * 4}s`,
                            animationDelay: `${-(i * 3.1)}s`,
                            fontSize: `${18 + (i % 4) * 8}px`
                        }}
                    >
                        {SUITS[i % SUITS.length]}
                    </span>
                ))}
            </div>

            <div className={styles.inner}>
                <motion.div
                    className={styles.header}
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className={styles.headerRow}>
                        <div className={styles.brandGroup}>
                            <div className={styles.brandIcon}>🎮</div>
                            <div>
                                <h1 className={styles.title}>Gaming Lobby</h1>
                                <p className={styles.subtitle}>Connect, play, and compete with friends</p>
                            </div>
                        </div>

                        <div className={styles.statusGroup}>
                            <div className={styles.statPill}>
                                <div className={styles.pulseDot} />
                                {onlineCount} online
                            </div>
                            <div className={styles.statPillMuted}>
                                🎮 {activeLobbiesCount} active {activeLobbiesCount === 1 ? 'lobby' : 'lobbies'}
                            </div>
                            {lobbyId ? (
                                <div className={styles.connectedBadge}>
                                    <div className={styles.connectedPill}>
                                        <div className={styles.pulseDot} />
                                        <span className={styles.connectedText}>Connected to: {lobbyName || lobbyId}</span>
                                    </div>
                                    <button className={styles.leaveButton} onClick={() => handleLeaveLobby(lobbyId)}>
                                        🚪 Leave Lobby
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.notConnectedBadge}>Not connected to lobby</div>
                            )}
                        </div>
                    </div>
                </motion.div>

                <div className={styles.mainGrid}>
                    <div className={styles.leftColumn}>
                        {lobbyId && (
                            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                                <LobbyRoom lobbyId={lobbyId} />
                            </motion.div>
                        )}
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
                            <LobbyTable />
                        </motion.div>
                        {!lobbyId && (
                            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                                <CreateLobbyForm />
                            </motion.div>
                        )}
                    </div>

                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                        <OnlineUsersWrapper lobbyId={lobbyId} />
                    </motion.div>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
                toastStyle={{
                    background: 'rgba(17, 24, 39, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(75, 85, 99, 0.3)',
                    borderRadius: '12px',
                    color: '#E5E7EB'
                }}
            />
        </div>
    );
};

export default Lobby;
